import { Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, Tab } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { STATEMENT_TYPE, StatusChip } from '../shared'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faClose } from '@fortawesome/pro-regular-svg-icons'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { StatementDetails, StatementSummary } from './StatementDetails'
import { Authorization } from './Authorization'
import { StatementDialogProvider, useDialogCache } from './StatementDialogContext'



const StatementDialogInner = ({
  open, onClose,
  statementNumber,
  type = STATEMENT_TYPE.daily,
  finalized, // this prop should not be required with a proper api implementation.
  ...props
}) => {
  const [activeTab, setActiveTab] = useState('details')
  const { currentPage, fetchNewPage, previous, hasCache, clearCache } = useDialogCache()
  const [loading, setLoading] = useState(true)

  const sections = currentPage?.sections || {}
  const data = sections?.info || {}
  const statementType = currentPage?.statementType || type

  const showTabs = (!data?.isFinalized
    && [STATEMENT_TYPE.daily, STATEMENT_TYPE.dailyPeriodic].includes(statementType))

  useEffect(() => {
    let isAlive = true
    setLoading(true)
    fetchNewPage(statementNumber, type, finalized, isAlive)
      .then(() => setLoading(false))
    return () => { isAlive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalized, statementNumber, type])

  const displayStmtNumber = useMemo(() => {
    switch (statementType) {
      case STATEMENT_TYPE.entry:
        return data.entryNumber
      case STATEMENT_TYPE.monthly:
        return data.pmsNumber
      default:
        return data.statementNumber
    }
  }, [statementType, data])

  const handleClose = (...args) => {
    setActiveTab("details")
    clearCache()
    onClose(...args)
  }

  return <Dialog open={open} onClose={handleClose}
    maxWidth='lg' fullWidth PaperProps={{ sx: { pb: 0 } }}
    closeAfterTransition {...props}
  >
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
      {hasCache && <IconButton onClick={previous}>
        <FontAwesomeIcon icon={faArrowLeft} width='1em' />
      </IconButton>}
      {statementType} Statement
      <Box component='pre' color='primary.main' fontSize='h4.fontSize'>
        {displayStmtNumber}
      </Box>
      {statementType !== STATEMENT_TYPE.entry &&
        <StatusChip finalized={data?.isFinalized} />
      }

      <IconButton onClick={handleClose} sx={{ ml: 'auto' }}>
        <FontAwesomeIcon icon={faClose} width='1em' />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ borderTop: 0, px: 3, pt: 0, pb: showTabs ? 0 : 3 }}>
      <StatementSummary {...data} type={statementType} />
      {showTabs
        ? <TabContext value={activeTab}>
          <TabList onChange={(e, n) => setActiveTab(n)}
            aria-label='statement dialog tabs'
            sx={{ mt: 1.5 }}
          >
            <Tab label='Statement Details' value='details' />
            <Tab label='Statement Authorizations' value='auths' />
          </TabList>
          <Divider />


          <TabPanel value='details' sx={{ px: 0 }}>
            <StatementDetails {...data} loading={loading} sectionData={sections} type={statementType} parent={displayStmtNumber} />
          </TabPanel>
          <TabPanel value='auths' sx={{ px: 0 }}>
            <Authorization {...data} firstEntryPaymentType={sections.entryList?.[0]?.paymentType} list={sections.statementAuthorizationList} />
          </TabPanel>
        </TabContext>
        : <StatementDetails {...data} loading={loading} sectionData={sections} type={statementType} parent={displayStmtNumber} />
      }
    </DialogContent>
  </Dialog>
}

export const StatementDialog = props => {
  return <StatementDialogProvider>
    <StatementDialogInner {...props} />
  </StatementDialogProvider>
}